import React, { useCallback, useEffect, useRef } from "react";

import { Box } from "@mui/material";
import { renderToString } from "react-dom/server";

interface PDFIframePreviewProps {
  children: React.ReactNode;
  iframeRef?: React.RefObject<HTMLIFrameElement | null>;
  onLoad?: () => void;
  dataTest?: string;
  title: string;
  sx?: object;
}

const scalePreviewContent = (iframe: HTMLIFrameElement) => {
  const doc = iframe.contentDocument;
  const content = doc?.querySelector<HTMLElement>(".preview-container");
  if (!doc || !content) {
    return;
  }
  const targetRatio = Number(content.dataset.aspectRatio);
  const scaleTweak = Number(content.dataset.scaleTweak || "1");
  const availableWidth = doc.body.clientWidth;
  const availableHeight = doc.body.clientHeight;
  if (!targetRatio || !availableWidth || !availableHeight) {
    return;
  }
  let containerWidth = availableWidth;
  let containerHeight = containerWidth / targetRatio;
  if (containerHeight > availableHeight) {
    containerHeight = availableHeight;
    containerWidth = containerHeight * targetRatio;
  }
  const scaleX = containerWidth / content.offsetWidth;
  const scaleY = containerHeight / content.offsetHeight;
  const scale = Math.min(scaleX, scaleY) * scaleTweak;
  doc.documentElement.style.setProperty("--scale-factor", scale.toString());
};

export const PDFIframePreview: React.FC<PDFIframePreviewProps> = ({
  children,
  iframeRef,
  onLoad,
  dataTest,
  title,
  sx,
}) => {
  const internalRef = useRef<HTMLIFrameElement | null>(null);
  const ref = iframeRef ?? internalRef;

  const handleLoad = useCallback(() => {
    if (ref.current) {
      scalePreviewContent(ref.current);
    }
    onLoad?.();
  }, [onLoad, ref]);

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) {
      return;
    }
    const observer = new ResizeObserver(() => scalePreviewContent(iframe));
    observer.observe(iframe);
    return () => observer.disconnect();
  }, [ref]);

  return (
    <Box
      component="iframe"
      ref={ref}
      data-test={dataTest}
      title={title}
      srcDoc={renderToString(children)}
      onLoad={handleLoad}
      sx={{
        width: "100%",
        height: "100%",
        border: "none",
        ...sx,
      }}
    />
  );
};
